/* eslint-disable @angular-eslint/directive-class-suffix */
import { Directive } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import {
  DomesticTravelCoverTypeCode,
  IFormFieldValidation,
  ITravelRestrictionConfig,
  ValidationType,
} from '@nibtravel/shared/util/models';
import { uniq } from 'lodash-es';

/**
 * Validator class to aggregate business logic in regards to destination validation
 *
 * NOTE: The class is now marked with Directive decorator as a preparation for Angular 10
 *
 * @export
 * @class DestinationValidator
 */
@Directive()
export class DestinationValidator {
  /**
   * The form field validation for restricted countries.
   * NOTE: The message is not assigned here as this message requires
   * state data since the control would only give the country codes.
   * The message should be handled on the implementation.
   *
   * Check implementation at qqc-destination.component.ts
   */
  static RestrictedValidation(
    config: ITravelRestrictionConfig,
    homeCountry: string
  ): IFormFieldValidation {
    return {
      type: ValidationType.Custom,
      custom: {
        name: 'destinationSupport',
        validator: () => this.Restricted(config, homeCountry),
      },
    };
  }

  private static Restricted(
    config: ITravelRestrictionConfig,
    homeCountry: string
  ): ValidatorFn {
    return (control: AbstractControl) =>
      control && control.value && control.value.length && config && homeCountry
        ? this.checkForRestrictions(control.value, config, homeCountry)
        : null;
  }

  private static checkForRestrictions(
    values: string[],
    config: ITravelRestrictionConfig,
    homeCountry: string
  ): Record<string, any> | null {
    const predicate = (code: string) => values.indexOf(code) !== -1;
    const unsafe = config.unsafeCountryCodes.filter(predicate);
    const sanctioned = config.sanctionedCountryCodes.filter(predicate);
    const noProduct = config.noProductCountryCodes.filter(
      (code: string) =>
        values.indexOf(code) !== -1 &&
        // ignore domestic travel errors (handled in domesticTravelSupport)
        (config.domesticTravelCoverTypeCode !==
          DomesticTravelCoverTypeCode.NotCovered ||
          code !== homeCountry)
    );

    if (unsafe.length > 0 || noProduct.length > 0 || sanctioned.length > 0) {
      return {
        destinationSupport: {
          unsafe: unsafe.length > 0 ? unsafe : undefined,
          noProduct: noProduct.length > 0 ? noProduct : undefined,
          sanctioned: sanctioned.length > 0 ? sanctioned : undefined,
          destinations: uniq([...unsafe, ...noProduct, ...sanctioned]),
        },
      };
    }

    return null;
  }
}
